/** What a person sets for this browser (DESIGN §16.7): the look, the unit weights show in, and Use AI (§34.3). */
import { useSyncExternalStore } from "react";
import { store, stored } from "./browser.js";

export type Theme = "system" | "light" | "dark";
export type UnitChoice = "logged" | "kg" | "lb";

export const LB = 0.45359237;

const THEME_KEY = "portal-theme";
const UNITS_KEY = "portal-units";
const AI_KEY = "portal-ai";

let theme: Theme = readTheme();
let units: UnitChoice = readUnits();
let useAi: boolean = readUseAi();
let version = 0;
const listeners = new Set<() => void>();

function readTheme(): Theme {
  const v = stored(THEME_KEY);
  return v === "light" || v === "dark" ? v : "system";
}

function readUnits(): UnitChoice {
  const v = stored(UNITS_KEY);
  return v === "kg" || v === "lb" ? v : "logged";
}

function readUseAi(): boolean {
  return stored(AI_KEY) !== "off";
}

function changed() {
  version++;
  for (const l of listeners) l();
}

export function applyTheme(): void {
  if (theme === "system") delete document.documentElement.dataset.theme;
  else document.documentElement.dataset.theme = theme;
}

export function setTheme(value: Theme): void {
  theme = value;
  store(THEME_KEY, value);
  applyTheme();
  changed();
}

export function setUnits(value: UnitChoice): void {
  units = value;
  store(UNITS_KEY, value);
  changed();
}

/** The assistant, `/fit` and Jev's line checks (§34.3); on unless turned off in this browser. */
export function setUseAi(on: boolean): void {
  useAi = on;
  store(AI_KEY, on ? "on" : "off");
  changed();
}

export function aiEnabled(): boolean {
  return useAi;
}

/** Re-renders on any change; returns the version, for memo keys. */
export function usePrefs(): { theme: Theme; units: UnitChoice; useAi: boolean; version: number } {
  useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => version,
  );
  return { theme, units, useAi, version };
}

/** Use AI, re-rendering when it changes: what gates every call that sends his lines out. */
export function useAiEnabled(): boolean {
  return usePrefs().useAi;
}

export function unitChoice(): UnitChoice {
  return units;
}

/** The unit a weight logged in `unit` is shown in. */
export function shownUnit(unit: string | null | undefined): string {
  return units === "logged" ? unit || "kg" : units;
}

/** A weight logged in `unit`, in the unit it is shown in, to 0.1 when converted. */
export function shownWeight(weight: number, unit: string | null | undefined): number {
  const from = unit || "kg";
  const to = shownUnit(unit);
  if (from === to) return weight;
  const kg = from === "lb" ? weight * LB : weight;
  return Math.round((to === "lb" ? kg / LB : kg) * 10) / 10;
}

/** For tests. */
export function resetPrefs(): void {
  theme = readTheme();
  units = readUnits();
  useAi = readUseAi();
  changed();
}
